// Document Repository — filter option + save/load APIs.
//
// The document TABLE itself is dummy (see ./repositoryData.js); everything in
// this file is REAL and reuses the same endpoints the Product Development
// Dashboard already uses:
//   • CMO options        → fetchCMOMembersAPI      (POST /fetchCMOMembers)
//   • MFG Plant options  → fetchMfgPlantsAPI       (GET  /fetchMfgPlants)
//   • Product options    → fetchProductsForCmoAPI  (GET  /fetchAllProductsForCMO/{id})
//   • Project→Campaign→Stage→Batch tree → fetchProjectsAPI (POST /fetchProjectDashboard)
//   • Save / load filters → /user/saveFilters, /user/fetchAllFilters (shared
//     cross-dashboard store) under our OWN key so the dashboard's saved filters
//     are never touched.

import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import { getApiBaseUrl } from '../../CommonMethods/CommonMethods';
import {
  fetchAllDashboardFilters,
  saveDashboardFilters,
  extractDashboardFilters,
} from '../../../../containers/utils/filterUtils';

// Reuse the dashboard's option/tree fetchers verbatim.
export { fetchCMOMembersAPI, fetchMfgPlantsAPI } from '../Dashboard/filtersAPI';
export { fetchProjectsAPI } from '../Dashboard/dashboardAPI';

// The Document Repository keeps its own saved-filter set, separate from the
// Product Development Dashboard's ('projectDevelopmentDashboard'). Saving posts
// only this key, so the backend merges it in and other dashboards are untouched.
export const DOC_REPO_FILTER_KEY = 'documentRepositoryDashboard';

// FSD ("Repository Filters and Scope Controls" -> Business Rules): "the
// Project and Campaign default to the user's most recently used values, or
// to the saved view where one exists" — a SEPARATE, softer concept from the
// explicit "Save view" above: it updates on every filter change, with no
// button click, and (per the FSD) must survive the user coming back later —
// not just closing and reopening the popup within the same browser tab. A
// distinct key keeps it independent of the deliberate saved view, using the
// exact same shared endpoint the Product Development Dashboard's own Save
// Filters uses (POST /user/saveFilters, GET /user/fetchAllFilters).
export const DOC_REPO_RECENT_KEY = 'documentRepositoryDashboardRecent';

// Product options for a CMO — GET /fetchAllProductsForCMO/{vendorMosymphonyID}.
// Response: { status, data: { productDetailsList: [{ collabrixProductID,
// productCode, productDescription }] } } (legacy productsList tolerated).
// CMO-scoped: returns nothing when no specific CMO is selected.
export const fetchProductsForCmoAPI = async (vendorMosymphonyID) => {
  try {
    if (
      vendorMosymphonyID == null ||
      vendorMosymphonyID === '' ||
      vendorMosymphonyID === 'all'
    ) {
      return { status: false, data: [] };
    }
    const apiBaseUrl = getApiBaseUrl(appUrl);
    if (!apiBaseUrl) return { status: false, data: [] };

    const response = await axios.get(
      `${apiBaseUrl}/fetchAllProductsForCMO/${vendorMosymphonyID}`
    );
    const list =
      response?.data?.data?.productDetailsList ||
      response?.data?.data?.productsList ||
      response?.data?.productDetailsList ||
      [];
    if (
      (response?.data?.status === true || response?.data?.status === 'true') &&
      Array.isArray(list)
    ) {
      return { status: true, data: list };
    }
    return { status: false, data: [] };
  } catch (error) {
    console.error('[DocumentRepository] Failed to load products for CMO:', error);
    return { status: false, data: [] };
  }
};

// Document list for the selected category + scope — POST /fetchPDRepositoryDocuments.
// Response: { status, data: { documents: [...row shape matches repositoryData.js's
// documented shape...], totalCount, categoryCounts: { <categoryKey>: count } } }.
export const fetchPDRepositoryDocumentsAPI = async ({
  category,
  campaignIds = [],
  stageId = 'all',
  batchNumber = 'all',
  search = '',
  page = 0,
  pageSize = 0,
}) => {
  try {
    const apiBaseUrl = getApiBaseUrl(appUrl);
    if (!apiBaseUrl) return { status: false, data: { documents: [], totalCount: 0, categoryCounts: {} } };

    const response = await axios.post(`${apiBaseUrl}/fetchPDRepositoryDocuments`, {
      category,
      campaignIds,
      stageId,
      batchNumber,
      search,
      page,
      pageSize,
    });
    if (response?.data?.status === true) {
      return { status: true, data: response.data.data };
    }
    return { status: false, data: { documents: [], totalCount: 0, categoryCounts: {} } };
  } catch (error) {
    console.error('[DocumentRepository] Failed to load documents:', error);
    return { status: false, data: { documents: [], totalCount: 0, categoryCounts: {} } };
  }
};

// Download a PD digitized document's original file — POST /downloadPDCustomFile.
// Response: { status, fileName, fileData: { type: "Buffer", data: [byte array] } },
// same shape as the existing downloadCustomFile/downloadChatFile endpoints.
export const downloadPDCustomFileAPI = async (collabrixPDCustomFileID) => {
  try {
    const apiBaseUrl = getApiBaseUrl(appUrl);
    if (!apiBaseUrl) return { status: false };

    const response = await axios.post(`${apiBaseUrl}/downloadPDCustomFile`, {
      collabrixPDCustomFileID,
    });
    return response?.data || { status: false };
  } catch (error) {
    console.error('[DocumentRepository] Failed to download document:', error);
    return { status: false };
  }
};

// Load this user's saved AND most-recently-used Document Repository filter
// selections in ONE round trip — both live under the same GET
// /user/fetchAllFilters response (fetchSavedRepoFiltersAPI and a since-removed
// fetchRecentRepoFiltersAPI each called fetchAllDashboardFilters separately,
// which fired the identical request twice on every popup open for no reason).
// extractDashboardFilters returns { status, save_filter_options } when a set
// exists for a given key, else { status: false } so the caller keeps its
// "All ----" defaults (first-time user).
export const fetchSavedAndRecentRepoFiltersAPI = async () => {
  const allFilters = await fetchAllDashboardFilters();
  return {
    saved: extractDashboardFilters(allFilters, DOC_REPO_FILTER_KEY),
    recent: extractDashboardFilters(allFilters, DOC_REPO_RECENT_KEY),
  };
};

// Persist a Document Repository filter selection. Always pass the FULL current
// selection — a save REPLACES the value stored under our key, so posting only a
// subset (e.g. just CMO/MFG/Product) would drop the rest.
export const saveRepoFiltersAPI = async (filters = {}) => {
  return saveDashboardFilters(DOC_REPO_FILTER_KEY, filters);
};

// Silently persist the current filter selection as "most recently used" on
// every change — no loading state, no error surfaced to the user (unlike
// saveRepoFiltersAPI's explicit "Save view" action), since this is a
// best-effort convenience, not something the user asked to happen.
export const saveRecentRepoFiltersAPI = async (filters = {}) => {
  return saveDashboardFilters(DOC_REPO_RECENT_KEY, filters);
};
